import { IOrderedSettings } from '@/interfaces/general/IOrderedSettings.ts';
import { INotificationLimiter } from '@/interfaces/powerMarkets/notifications/INotificationLimiter.ts';
import { ISubscriptionSettings } from '@/interfaces/powerMarkets/notifications/ISubscriptionSettings.ts';
import { resolveSubscriptionBrowserSoundChoice } from '@/utils/notifications/notificationAudio.ts';
import { NumericBoolean } from '@/types/general/NumericBoolean.ts';

const DEFAULT_LIMITER: INotificationLimiter = { selected: 0, volume: 0 };

// TEMP dual-run: old Enact new-sub defaults used typo key `nbd`; backend expects `ndb`.
// Delete this type + all `nbd` mapping below once old site is gone and leftover `nbd` docs are gone/rewritten.
type SubscriptionWithLegacyNbd = ISubscriptionSettings & { nbd?: NumericBoolean | null };

function toNumericBoolean (value: unknown): NumericBoolean {
    return value === 1 || value === true ? 1 : 0;
}

function normalizeLimiterVolume (volume: unknown): INotificationLimiter['volume'] {
    if (volume === null || volume === undefined || volume === '') return 0;
    if (volume === 0) return 0;
    if (typeof volume === 'string') return volume;
    if (typeof volume === 'number' && !Number.isNaN(volume)) {
        return volume === 0 ? 0 : String(volume);
    }
    return 0;
}

/**
 * Always persist limiter objects — never null.
 * TEMP dual-run: old site leaves limiters null until first edit. Safe to simplify/remove
 * null-coercion once old site is gone and leftover null-limiter docs are gone/rewritten
 * (Spark already writes objects on create/save).
 */
export function normalizeNotificationLimiter (
    limiter: INotificationLimiter | null | undefined,
): INotificationLimiter {
    if (limiter == null || typeof limiter !== 'object') {
        return { ...DEFAULT_LIMITER };
    }

    return {
        selected: toNumericBoolean(limiter.selected),
        volume: normalizeLimiterVolume(limiter.volume),
    };
}

/**
 * Load/save hygiene for dual-run notification docs.
 * TEMP dual-run: delete `nbd`→`ndb` mapping (and SubscriptionWithLegacyNbd) when old site is gone.
 * Limiter null-coercion can go in the same cleanup pass.
 */
export function normalizeSubscriptionSettings (
    subscription: ISubscriptionSettings,
): ISubscriptionSettings {
    const legacy = subscription as SubscriptionWithLegacyNbd;
    // TEMP dual-run: strip typo key `nbd` so it is never re-persisted.
    const { nbd, ...withoutTypo } = legacy;

    const existingNdb = withoutTypo.ndb;
    let ndb: NumericBoolean = toNumericBoolean(existingNdb);
    // TEMP dual-run: copy legacy `nbd` into `ndb` when `ndb` is missing/zero.
    if ((existingNdb == null || existingNdb === 0) && nbd != null && nbd !== 0) {
        ndb = toNumericBoolean(nbd);
    }

    const browserSoundChoice = resolveSubscriptionBrowserSoundChoice(withoutTypo);
    const browserSound: NumericBoolean = browserSoundChoice === 'None' ? 0 : 1;

    return {
        ...withoutTypo,
        ndb,
        browserSoundChoice,
        browserSound,
        lowValue: normalizeNotificationLimiter(withoutTypo.lowValue),
        highValue: normalizeNotificationLimiter(withoutTypo.highValue),
        secondaryLowValue: normalizeNotificationLimiter(withoutTypo.secondaryLowValue),
        secondaryHighValue: normalizeNotificationLimiter(withoutTypo.secondaryHighValue),
    };
}

/** TEMP dual-run: whole ordered-settings pass can go when normalizeSubscriptionSettings is removed. */
export function normalizeSubscriptionsWithOrder (
    subscriptionsWithOrder: IOrderedSettings<ISubscriptionSettings>,
): IOrderedSettings<ISubscriptionSettings> {
    const objectsToStore: IOrderedSettings<ISubscriptionSettings>['objectsToStore'] = {};

    Object.entries(subscriptionsWithOrder.objectsToStore).forEach(([id, pageData]) => {
        if (!pageData?.data) {
            objectsToStore[id] = pageData;
            return;
        }

        objectsToStore[id] = {
            ...pageData,
            data: normalizeSubscriptionSettings(pageData.data),
        };
    });

    return {
        ...subscriptionsWithOrder,
        objectsToStore,
    };
}
